import type { ReactNode } from "react";

import { useEffect, useRef } from "react";

import { Button } from "./Button";
import { Icon } from "./Icon";
import { TruncatedText } from "./TruncatedText";
import { notify } from "./toast-store";
import { colors } from "./colors";

export interface SaveStatusProps {
  dirty: boolean;
  pending?: boolean;
  saveDisabled?: boolean;
  discardDisabled?: boolean;
  error?: string;
  saved?: boolean;
  invalid?: boolean;
  invalidMessage?: string;
  invalidStatus?: ReactNode;
  persistent?: boolean;
  mobileFixed?: boolean;
  warnings?: readonly string[];
  warningStatusLabel?: (warnings: readonly string[], saved: boolean) => ReactNode;
  conflict?: { message: string; reloadLabel: string; onReload: () => void; reloadPending?: boolean };
  discardOnConflict?: boolean;
  footer?: ReactNode;
  destructive?: ReactNode;
  onSave: () => void;
  onInvalidSave?: () => void;
  onDiscard: () => void;
  saveLabel: string;
  discardLabel: string;
  savedLabel: string;
  pendingLabel: string;
  saveDescribedBy?: string;
  saveTitle?: string;
  saveLocked?: boolean;
}

export function SaveStatus({
  dirty,
  pending = false,
  saveDisabled = false,
  discardDisabled = false,
  error,
  saved = false,
  invalid = false,
  invalidMessage,
  invalidStatus,
  persistent = false,
  mobileFixed = false,
  warnings = [],
  warningStatusLabel,
  conflict,
  discardOnConflict = false,
  footer,
  destructive,
  onSave,
  onInvalidSave,
  onDiscard,
  saveLabel,
  discardLabel,
  savedLabel,
  pendingLabel,
  saveDescribedBy,
  saveTitle,
  saveLocked = false,
}: SaveStatusProps) {
  const lastToastedMessage = useRef<string | null>(null);
  const longError = conflict?.message ?? error;
  const visible = persistent || dirty || saved;

  useEffect(() => {
    if (!visible || longError === undefined || longError.length < 80) {
      lastToastedMessage.current = null;
      return;
    }
    if (lastToastedMessage.current === longError) return;
    lastToastedMessage.current = longError;
    notify({ tone: "error", message: longError });
  }, [longError, visible]);

  if (!persistent && !dirty && !saved) return null;

  const warningStatus = warnings.length === 0 ? null : warningStatusLabel?.(warnings, saved && !dirty) ?? warnings[0];
  const invalidLockReason = saveLocked && invalid && dirty ? invalidMessage : undefined;
  const statusText = conflict !== undefined
    ? `× ${conflict.message}`
    : pending
      ? pendingLabel
      : error !== undefined
        ? `× ${error}`
        : saved && !dirty
          ? warningStatus ?? savedLabel
          : invalid && dirty
            ? invalidStatus ?? (invalidMessage === undefined ? "" : `× ${invalidMessage}`)
            : dirty
              ? warningStatus
              : null;
  const invalidAction = persistent && invalid && dirty && !pending && !saveDisabled && conflict === undefined;
  const isSaveDisabled = pending || saveDisabled || conflict !== undefined || !dirty;
  const showButtons = dirty || persistent;
  const footerOnly = statusText === null && footer !== undefined && conflict === undefined;
  const showFooter = footer !== undefined && conflict === undefined && invalidLockReason === undefined;
  return (
    <div data-ui="" className={`ui-save-bar${persistent ? " ui-save-bar--persistent" : ""}${mobileFixed ? " ui-save-bar--mobile-fixed" : ""}${error === undefined ? "" : " ui-save-bar--error"}`} aria-busy={pending}>
      <div className={`ui-save-bar__status${footerOnly ? " ui-save-bar__status--footer-only" : ""}`} role="status" aria-live="polite">
        {warningStatus !== null && statusText === warningStatus || warningStatus !== null && saved && !dirty ? <Icon name="warning" size={16} /> : null}
        {statusText === null ? null : <div className="ui-save-bar__message" style={{ color: conflict !== undefined || error !== undefined || (invalid && dirty) ? colors.errorText : warningStatus !== null ? colors.amber : colors.text3 }}>
          {typeof statusText === "string" ? <TruncatedText className="ui-save-bar__message-copy" text={statusText} /> : statusText}
        </div>}
        {!showFooter ? null : <div className="ui-save-bar__footer" title={typeof footer === "string" ? footer : undefined}>{footer}</div>}
      </div>
      {showButtons || destructive !== undefined ? (
        <div className={`ui-save-bar__actions${destructive === undefined ? "" : " ui-save-bar__actions--destructive"}`}>
          {destructive}
          <div className="ui-save-bar__buttons">
            {showButtons && (conflict === undefined || discardOnConflict) ? <Button className="ui-save-bar__discard" variant="subtle" onClick={onDiscard} disabled={!dirty || pending || discardDisabled} ariaLabel={discardLabel} title={discardLabel}>{discardLabel}</Button> : null}
            {showButtons ? conflict === undefined ? <Button
              className="ui-save-bar__save"
              variant={persistent && (invalid || saveLocked) ? "neutral" : "primary"}
              {...(saveLocked ? { icon: "lock" as const } : {})}
              onClick={invalidAction && onInvalidSave !== undefined ? onInvalidSave : onSave}
              disabled={isSaveDisabled && !invalidAction}
              ariaDisabled={invalidAction}
              ariaLabel={saveLabel}
              title={invalidLockReason ?? saveTitle ?? saveLabel}
              {...(saveDescribedBy === undefined ? {} : { describedBy: saveDescribedBy })}
            >
              <span className="ui-save-bar__save-labels">
                <span aria-hidden={pending}>{saveLabel}</span>
                <span aria-hidden={!pending}>{pendingLabel}</span>
              </span>
            </Button> : <Button className="ui-save-bar__reload" variant="neutral" icon="reload" onClick={conflict.onReload} disabled={pending || conflict.reloadPending === true}>{conflict.reloadLabel}</Button> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
